/**
 * ScriptStoryHeader — the Script Writer node's story-summary header.
 *
 * Two parts:
 *   - a title block: the agent-coloured "Story" eyebrow, the project name, and
 *     the run-status chip;
 *   - up to four collapsible metadata rows (Logline, Genre / Tone, Synopsis,
 *     Themes) fed by the `out_logline` / `out_genre_tone` / `out_synopsis` /
 *     `out_themes` ports.
 *
 * Rows render only when their port has content, so a screenplay generated before
 * these ports existed shows the title block alone rather than four empty rows.
 */

import { memo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../../lib/utils'

export interface StoryMetadataRow {
  /** Stable key — i18n lookup and `data-testid` suffix. */
  id: 'logline' | 'genreTone' | 'synopsis' | 'themes'
  /** Row text. Blank/undefined rows are dropped by the caller. */
  value: string
}

interface ScriptStoryHeaderProps {
  /** Project name (`project.name`), not the node's `in_name`. */
  title: string
  /** Shown while the project query is in flight or the project is unnamed. */
  titleFallback: string
  /** Status chip rendered at the top-right of the title block. */
  statusChip: ReactNode
  /** Metadata rows, in display order. Pass only rows that have content. */
  rows: StoryMetadataRow[]
  /** Agent colour for the "Story" eyebrow. */
  accentColor: string
}

const MetadataRow = memo(function MetadataRow({ row }: { row: StoryMetadataRow }) {
  const { t } = useTranslation('canvas')
  const [expanded, setExpanded] = useState(false)
  const label = t(`scriptWriterNode.storyMetadata.${row.id}`)

  return (
    <div className="border-b border-border-subtle last:border-b-0">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setExpanded((prev) => !prev)
        }}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 px-4 py-1.5 text-left transition-colors hover:bg-surface-1 nodrag"
        data-testid={`story-metadata-toggle-${row.id}`}
      >
        <svg
          className="h-3 w-3 shrink-0 text-text-muted"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points={expanded ? '18 15 12 9 6 15' : '6 9 12 15 18 9'} />
        </svg>
        <span className="shrink-0 text-xs font-medium text-text-muted">{label}:</span>
        {!expanded && (
          <span className="min-w-0 flex-1 truncate text-xs font-medium text-text-secondary">{row.value}</span>
        )}
      </button>
      {expanded && (
        <div
          className="px-4 pb-2 pl-9 nodrag nowheel"
          onWheel={(e) => e.stopPropagation()}
          data-testid={`story-metadata-expanded-${row.id}`}
        >
          <div className="max-h-[120px] overflow-y-auto text-xs font-medium leading-relaxed text-text-secondary">
            {row.value}
          </div>
        </div>
      )}
    </div>
  )
})

const ScriptStoryHeader = memo(function ScriptStoryHeader({
  title,
  titleFallback,
  statusChip,
  rows,
  accentColor,
}: ScriptStoryHeaderProps) {
  const { t } = useTranslation('canvas')

  return (
    <>
      <div className="px-4 py-3 border-b border-border-subtle" data-testid="script-story-header">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div
              className="text-xs font-medium leading-none"
              style={{ color: accentColor }}
              data-testid="script-story-eyebrow"
            >
              {t('scriptWriterNode.storyEyebrow')}
            </div>
            <h3
              className={cn(
                'mt-1 truncate text-xl font-semibold',
                title ? 'text-text-primary' : 'text-text-muted',
              )}
              data-testid="script-story-title"
            >
              {title || titleFallback}
            </h3>
          </div>
          {statusChip}
        </div>
      </div>
      {rows.length > 0 && (
        <div className="border-b border-border-subtle" data-testid="story-metadata-rows">
          {rows.map((row) => (
            <MetadataRow key={row.id} row={row} />
          ))}
        </div>
      )}
    </>
  )
})

ScriptStoryHeader.displayName = 'ScriptStoryHeader'

export { ScriptStoryHeader }
